"use client";

import type { AbstractIntlMessages } from "next-intl";
import { NextIntlClientProvider } from "next-intl";
import type { ReactElement, ReactNode } from "react";
import type { Locale } from "./locales";
import { humanizeMessageKey } from "./message-fallback";

/**
 * `NextIntlClientProvider` with a readable fallback for unresolved messages.
 *
 * Exists as its own client component because `getMessageFallback` is a
 * function, and a function cannot be passed from the server layout across the
 * client boundary. Without it, every client component falls back to next-intl's
 * default and prints `namespace.key` into the interface.
 *
 * The trade-off: rendered from a client component, `NextIntlClientProvider` is
 * the bare version that infers **nothing** — the server variant
 * (`NextIntlClientProviderServer`) is what normally injects locale, messages
 * and time zone from `i18n/request.ts`. So all three are handed in explicitly
 * by the layout; leaving any of them out throws at runtime (locale) or silently
 * drops every translation (messages) and un-pins the time zone.
 */
export function IntlProvider({
  locale,
  messages,
  timeZone,
  children,
}: {
  readonly locale: Locale;
  readonly messages: AbstractIntlMessages;
  readonly timeZone: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      getMessageFallback={humanizeMessageKey}
    >
      {children}
    </NextIntlClientProvider>
  );
}
